
import { Pencil, Trash2, Calendar, Users, Sparkles } from "lucide-react";
import Badge from "@/app/components/Badge";
import { EnquiryData } from "./EnquiryTable";
import { formatDate } from "@/lib/data";

interface EnquiryCardProps {
  enquiries: EnquiryData[];
  isStaff: boolean;
  canDelete: boolean;
  onEdit: (enquiry: EnquiryData) => void;
  onConvert: (enquiry: EnquiryData) => void;
  onDelete: (id: number) => void;
  onViewDetails?: (id: number) => void;
}

export default function EnquiryCard({
  enquiries,
  isStaff,
  canDelete,
  onEdit,
  onConvert,
  onDelete,
  onViewDetails,
}: EnquiryCardProps) {
  return (
    <div className="md:hidden space-y-3">
      {enquiries.map((e: any) => {
        const name = e.client_name || e.client?.name || e.name || "Unknown";
        const phone = e.client_phone || e.client?.phone || e.phone || "";
        const eventType = e.type || e.event_type || "-";
        const dateStr = e.event_date ? formatDate(e.event_date) : "-";
        const guests = e.guest_count || e.guests || 0;

        return (
        <div key={e.id} className="bg-white rounded-xl border border-stone-100 p-4 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm bg-amber-500 flex-shrink-0">
                {name.charAt(0) || "?"}
              </div>
              <div className="min-w-0">
                <div className="font-semibold text-stone-900 text-sm truncate">{name}</div>
                <div className="text-xs text-stone-500 truncate">{phone}</div>
              </div>
            </div>
            <Badge label={e.status} status={e.status} />
          </div>
          <div className="grid grid-cols-3 gap-2 mb-4 text-xs">
            <div className="flex items-center gap-2 text-stone-600 bg-stone-50 rounded-lg p-2">
              <Calendar className="w-3.5 h-3.5 text-stone-400" />
              <span className="truncate">{dateStr}</span>
            </div>
            <div className="flex items-center gap-2 text-stone-600 bg-stone-50 rounded-lg p-2">
              <Users className="w-3.5 h-3.5 text-stone-400" />
              <span className="truncate">{guests}</span>
            </div>
            <div className="flex items-center gap-2 text-stone-600 bg-stone-50 rounded-lg p-2">
              <Sparkles className="w-3.5 h-3.5 text-stone-400" />
              <span className="truncate">{eventType}</span>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onEdit(e)}
              className="flex-1 flex items-center justify-center gap-2 text-xs px-3 py-2.5 rounded-lg bg-stone-100 text-stone-700 font-semibold hover:bg-stone-200 transition-colors"
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </button>
            {(e.status === 'new' || e.status === 'qualified') && (
              <button
                onClick={() => onConvert(e)}
                className="flex-1 text-xs px-3 py-2.5 rounded-lg text-white font-semibold bg-amber-500 hover:bg-amber-600 transition-colors"
              >
                Convert
              </button>
            )}
            {!isStaff && canDelete && (
              <button
                onClick={() => onDelete(e.id)}
                className="px-3 py-2.5 rounded-lg text-red-600 bg-red-50 hover:bg-red-100 transition-colors"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        );
      })}
      {enquiries.length === 0 && (
        <div className="text-center py-12 text-stone-400 bg-white rounded-xl border border-stone-100 text-sm">
          <span className="text-stone-300">📭</span><br/>
          No enquiries found.
        </div>
      )}
    </div>
  );
}
